// Typed access to the static data files in src/data/*.json.

import type {
  Ability,
  AccusationTrial,
  AntiPlayerStrategy,
  Battle,
  Dialogue,
  Faction,
  FactionAction,
  GameEvent,
  JournalEntry,
  KnowledgeCategory,
  Location,
  Route,
  SpiritQuestion,
  SpiritScene,
  Unit,
} from "../types"
import abilitiesData from "../data/abilities.json"
import accusationsData from "../data/accusations.json"
import antiPlayerData from "../data/antiPlayer.json"
import battlesData from "../data/battles.json"
import spiritQuestionsData from "../data/spiritQuestions.json"
import spiritScenesData from "../data/spiritScenes.json"
import dialoguesData from "../data/dialogues.json"
import dialoguesAntiPlayerData from "../data/dialoguesAntiPlayer.json"
import dialoguesSpiritData from "../data/dialoguesSpirit.json"
import dialoguesSpiritQuestionsData from "../data/dialoguesSpiritQuestions.json"
import dialoguesPhase7Data from "../data/dialoguesPhase7.json"
import eventsData from "../data/events.json"
import factionsData from "../data/factions.json"
import factionActionsData from "../data/factionActions.json"
import journalData from "../data/journal.json"
import locationsData from "../data/locations.json"
import routesData from "../data/routes.json"
import unitsData from "../data/units.json"

export const locations: Location[] = locationsData as Location[]
export const routes: Route[] = routesData as Route[]
export const units: Unit[] = unitsData as Unit[]
export const abilities: Ability[] = abilitiesData as Ability[]
export const factions: Faction[] = factionsData as Faction[]
export const events: GameEvent[] = eventsData as GameEvent[]
// Phase 6's spirit and anti-player writing is kept in its own files purely so
// no single dialogue file becomes unreviewable. They are one namespace at
// runtime, and ids must stay unique across all of them.
export const dialogues: Dialogue[] = [
  ...(dialoguesData as Dialogue[]),
  ...(dialoguesSpiritData as Dialogue[]),
  ...(dialoguesSpiritQuestionsData as Dialogue[]),
  ...(dialoguesAntiPlayerData as Dialogue[]),
  ...(dialoguesPhase7Data as Dialogue[]),
]
export const factionActions: FactionAction[] = factionActionsData as FactionAction[]
export const battles: Battle[] = battlesData as Battle[]
export const journalEntries: JournalEntry[] = journalData as JournalEntry[]
export const spiritScenes: SpiritScene[] = spiritScenesData as SpiritScene[]
export const spiritQuestions: SpiritQuestion[] = spiritQuestionsData as SpiritQuestion[]
export const antiPlayerStrategies: AntiPlayerStrategy[] =
  antiPlayerData as AntiPlayerStrategy[]
export const accusationTrials: AccusationTrial[] = accusationsData as AccusationTrial[]

function indexById<T extends { id: string }>(items: T[]): Map<string, T> {
  return new Map(items.map((item) => [item.id, item]))
}

const locationsById = indexById(locations)
const routesById = indexById(routes)
const unitsById = indexById(units)
const abilitiesById = indexById(abilities)
const factionsById = indexById(factions)
const battlesById = indexById(battles)
const dialoguesById = indexById(dialogues)
const eventsById = indexById(events)
const journalEntriesByKnowledgeId = new Map(
  journalEntries.map((entry) => [entry.knowledgeId, entry]),
)

export function getLocationById(id: string): Location | undefined {
  return locationsById.get(id)
}

export function getRouteById(id: string): Route | undefined {
  return routesById.get(id)
}

export function getUnitById(id: string): Unit | undefined {
  return unitsById.get(id)
}

export function getAbilityById(id: string): Ability | undefined {
  return abilitiesById.get(id)
}

export function getFactionById(id: string): Faction | undefined {
  return factionsById.get(id)
}

export function getBattleById(id: string): Battle | undefined {
  return battlesById.get(id)
}

export function getEventById(id: string): GameEvent | undefined {
  return eventsById.get(id)
}

export function getDialogueById(id: string): Dialogue | undefined {
  return dialoguesById.get(id)
}

const accusationTrialsById = indexById(accusationTrials)

export function getAccusationTrialById(id: string): AccusationTrial | undefined {
  return accusationTrialsById.get(id)
}

export function getJournalEntryByKnowledgeId(knowledgeId: string): JournalEntry | undefined {
  return journalEntriesByKnowledgeId.get(knowledgeId)
}

// Knowledge ids that do not fall out of a naming convention, mapped to their
// category by hand. Everything with a regular prefix/suffix (anti_player_*,
// heard_*, resolved_*, *_subjected, *_survived, met_*, ritual_deepens_*) is
// handled by the rules in getKnowledgeCategory and deliberately kept out of
// here, so those growing families never need a line each.
const KNOWLEDGE_CATEGORY_OVERRIDES: Record<string, KnowledgeCategory> = {
  // Foreseen scheduled attacks — a threat the player has lived through once.
  dragon_raids_ironwatch_day22: "omen",
  trolls_raid_goldenfields_day26: "omen",
  dark_elves_raid_rivergate_day33: "omen",
  anchor_rises_at_temple_day38: "omen",
  giants_take_kings_road_day44: "omen",
  rift_opens_at_blacktooth_day50: "omen",
  orcs_attack_dawnmere_day2: "omen",
  orcs_raid_goldenfields_day12: "omen",
  orcs_besiege_ironwatch_day15: "omen",
  // Hard rules of war.
  learned_unit_counters: "doctrine",
  realms_can_be_mustered: "doctrine",
  orcs_had_outside_help: "doctrine",
  // The reset itself, and the spirit's readings of it.
  met_the_spirit: "loop",
  spirit_explained_loop: "loop",
  spirit_theory_loop_is_ritual_wake: "loop",
  // The ritual eating the light, and the day it finishes.
  deadline_revealed: "deadline",
  saw_ritual_darkening: "deadline",
  saw_the_sun_go_out: "deadline",
  // Histories, records and mysteries uncovered by investigation.
  dawnmere_ruins_secret: "lore",
  scouted_blacktooth_wastes: "lore",
  crownreach_history_context: "lore",
  exposed_false_crownreach_history: "lore",
  temple_first_dawn_lore: "lore",
  history_dawnmere_conspiracy_glimpse: "lore",
  temple_records_prove_ritual_anchors_loop: "lore",
  // Word sent that turned a raid before it landed — the one entry whose whole
  // substance is a dispatch rather than a battle.
  goldenfields_raid_prevented_outright: "dispatch",
  // Doors opened and standing gained with the realms (met_* / *_subjected are
  // handled by rule; these are the audiences and writs that are not).
  crownreach_audience_obtained: "alliance",
  crownreach_service_pledged: "alliance",
  moonspire_audience_granted: "alliance",
  visited_cloudwood_aerie: "alliance",
  // Ways charted through the world.
  roads_charted: "route",
  ebon_citadel_north_cistern_route: "route",
  ebon_citadel_approach_known: "route",
  // The rival — knows_how_to_sever_* does not carry the anti_player_ prefix.
  knows_how_to_sever_anti_player: "rival",
  // The citadel reached, and the loop finally won.
  stood_before_ebon_citadel: "triumph",
  breached_ebon_citadel: "triumph",
  campaign_won: "triumph",
}

/**
 * The category of a knowledge entry, used to hang a small icon beside it in the
 * Journal. Regular naming families are matched by rule so they never need
 * per-id upkeep; everything else is looked up in KNOWLEDGE_CATEGORY_OVERRIDES.
 * Returns null for an id with no home yet, in which case the line simply shows
 * no icon — the same graceful blank the sprite sheets already use.
 */
export function getKnowledgeCategory(knowledgeId: string): KnowledgeCategory | null {
  // Explicit assignments win first, so an exception like met_the_spirit (the one
  // met_* that is the spirit and not a realm) is not swept up by the family rule
  // for its prefix below.
  const override = KNOWLEDGE_CATEGORY_OVERRIDES[knowledgeId]
  if (override) return override
  if (knowledgeId.startsWith("anti_player_")) return "rival"
  // Disproving one of his forgeries before a realm's court is a mark against
  // the rival, and a record of foreknowledge the player carries into later loops.
  if (knowledgeId.startsWith("disproved_forgery_")) return "rival"
  if (knowledgeId.startsWith("heard_")) return "rumor"
  if (knowledgeId.startsWith("resolved_")) return "victory"
  if (knowledgeId.startsWith("ritual_deepens_")) return "deadline"
  if (knowledgeId.endsWith("_survived")) return "victory"
  if (knowledgeId.endsWith("_subjected")) return "alliance"
  if (knowledgeId.startsWith("met_")) return "alliance"
  return null
}

export function getRoutesForLocation(locationId: string): Route[] {
  return routes.filter(
    (route) =>
      route.fromLocationId === locationId || route.toLocationId === locationId,
  )
}
